import React from 'react'
import { t } from 'ttag'
import { Modal } from 'components/shared/Modal/Modal'
import { TRAITS } from 'components/common/ScoreCard/ScoreCard'
import {
  languages,
  translateTrait,
} from 'components/common/ScoreCard/descriptions'
import cx from 'classnames'
import { Accordion } from 'components/shared/Accordion/Accordion'
import styles from './TalentAreaDetailModal.module.scss'

const competencyParentTrait = Object.entries(TRAITS).reduce(
  (acc, [parent, codes]) => {
    codes.forEach(code => {
      acc[code] = parent
    })
    return acc
  },
  {},
)

const recruiterTraitLabel = (traitKey, language) =>
  languages[language]?.recruiter?.traits?.[traitKey]?.label ?? traitKey

const AreaDetailCompetencyBlock = ({ code, language, tone }) => {
  const parentKey = competencyParentTrait[code]
  const { label, abstract } = translateTrait(code, 0.5, language, false)
  const paragraphs = Array.isArray(abstract) ? abstract : []

  return (
    <div className={cx(styles.areaDetailCompetencyBlock)}>
      <div className={styles.areaDetailCompetencyName}>
        <span className={styles.areaDetailCompetencyNameText}>{label}</span>
        <span className={styles.areaDetailCompetencyNameParent}>
          ({recruiterTraitLabel(parentKey, language)})
        </span>
      </div>
      <div className={styles.areaDetailCompetencyAbstract}>
        {paragraphs.map((para, idx) => (
          <p key={idx}>{para}</p>
        ))}
      </div>
    </div>
  )
}

const AreaDetailContributorsSection = ({
  language,
  topContributorCodes,
  bottomContributorCodes,
}) => (
  <div className={styles.areaDetailCompetencies}>
    <Accordion className={styles.areaDetailAccordion} isInitiallyOpen>
      <Accordion.Header
        className={styles.areaDetailAccordionHeader}
        buttonClassName={styles.areaDetailAccordionButton}
        wrapperClassName={styles.areaDetailSectionTitle}
        title={t`Most important personality facets`}
      />
      <Accordion.Panel className={styles.areaDetailAccordionPanel}>
        <p className={styles.areaDetailFacetIntro}>
          {t`The 5 personality facets that contribute most to this talent area:`}
        </p>
        <div className={styles.areaDetailAccordionPanelInner}>
          {topContributorCodes.map(code => (
            <AreaDetailCompetencyBlock
              key={code}
              code={code}
              language={language}
              tone="top"
            />
          ))}
        </div>
      </Accordion.Panel>
    </Accordion>
    <Accordion className={styles.areaDetailAccordion} isInitiallyOpen={false}>
      <Accordion.Header
        className={styles.areaDetailAccordionHeader}
        buttonClassName={styles.areaDetailAccordionButton}
        wrapperClassName={styles.areaDetailSectionTitle}
        title={t`Least relevant personality facets`}
      />
      <Accordion.Panel className={styles.areaDetailAccordionPanel}>
        <p className={styles.areaDetailFacetIntro}>
          {t`The 5 personality facets that contribute least to this talent area:`}
        </p>
        <div className={styles.areaDetailAccordionPanelInner}>
          {bottomContributorCodes.map(code => (
            <AreaDetailCompetencyBlock
              key={code}
              code={code}
              language={language}
              tone="bottom"
            />
          ))}
        </div>
      </Accordion.Panel>
    </Accordion>
  </div>
)

export const TalentAreaDetailModal = ({
  areaRow,
  contributions,
  language,
  onClose,
}) => {
  const isOpen = areaRow !== null
  const areaKey = areaRow?.code
  const contributors = areaKey ? contributions?.[areaKey] : null

  const topContributorCodes = Array.isArray(contributors?.top_contributors)
    ? contributors.top_contributors
    : []
  const bottomContributorCodes = Array.isArray(
    contributors?.bottom_contributors,
  )
    ? contributors.bottom_contributors
    : []

  return (
    <Modal
      nestedModalEscapeCapture
      className={styles.areaDetailModal}
      cancelLabel={t`Close`}
      isOpen={isOpen}
      portalClassName={styles.areaDetailPortal}
      title={areaRow?.area ?? ''}
      onCancel={onClose}
      onClose={onClose}
    >
      <Modal.Header />
      <Modal.Content className={styles.areaDetailContent}>
        {areaRow?.description ? (
          <div className={styles.areaDetailCompetencyGroup}>
            <div className={styles.areaDetailSectionTitle}>
              {t`Description`}
            </div>
            <p className={styles.areaDetailDescription}>
              {areaRow.description}
            </p>
          </div>
        ) : null}
        <AreaDetailContributorsSection
          key={areaRow?.code}
          language={language || 'en'}
          topContributorCodes={topContributorCodes}
          bottomContributorCodes={bottomContributorCodes}
        />
      </Modal.Content>
    </Modal>
  )
}
